import { useEffect, useRef, useState } from 'react';
import { Input, Button, Tooltip, DatePicker } from '@bancobarigui/barihaus';
import {
  SearchOutlined,
  ClearOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { DetailFilters as DetailFiltersType } from '../useDashboardDetail';

interface FilterOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface DetailFiltersProps {
  filters: DetailFiltersType;
  statusOptions: FilterOption[];
  channelOptions?: FilterOption[];
  cityOptions?: FilterOption[];
  inspectionTypeOptions?: FilterOption[];
  propertyTypeOptions?: FilterOption[];
  inspectionReasonOptions?: FilterOption[];
  providerOptions?: FilterOption[];
  isLoading: boolean;
  onFilterChange: (field: keyof DetailFiltersType, value: string | undefined) => void;
  onClear: () => void;
  onApply: () => void;
}

export function DetailFilters({
  filters,
  isLoading,
  onFilterChange,
  onClear,
  onApply,
}: DetailFiltersProps) {
  // A busca é livre e roda no servidor a cada mudança de `filters`. Sem debounce,
  // cada tecla dispararia uma consulta. O input passa a ter estado próprio e só
  // propaga para o filtro depois que o usuário para de digitar.
  const [searchTerm, setSearchTerm] = useState(filters.searchCode ?? '');
  const isTypingRef = useRef(false);

  // Mantém o input em sincronia quando o filtro muda de fora (ex.: "Limpar filtros").
  useEffect(() => {
    if (!isTypingRef.current) {
      setSearchTerm(filters.searchCode ?? '');
    }
  }, [filters.searchCode]);

  useEffect(() => {
    if (!isTypingRef.current) return;

    const timer = setTimeout(() => {
      isTypingRef.current = false;
      onFilterChange('searchCode', searchTerm.trim() || undefined);
    }, 450);

    return () => clearTimeout(timer);
  }, [searchTerm, onFilterChange]);

  const handleSearchChange = (value: string) => {
    isTypingRef.current = true;
    setSearchTerm(value);
  };

  const handleStartDateChange = (date: dayjs.Dayjs | null) => {
    onFilterChange('startDate', date?.format('YYYY-MM-DD'));
  };

  const handleEndDateChange = (date: dayjs.Dayjs | null) => {
    onFilterChange('endDate', date?.format('YYYY-MM-DD'));
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6 shadow-sm">
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr_1fr_auto] gap-4 items-end">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Buscar
          </label>
          <Tooltip title="Busca em proposta, código, squad, proponente, CPF/CNPJ, endereço, cidade, matrícula, motivo, canal e status. Separe por espaço para combinar termos (ex.: silva curitiba).">
            <Input
              placeholder="Proposta, proponente, squad, endereço, CPF/CNPJ, status..."
              prefix={<SearchOutlined className="text-gray-400" />}
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="rounded-lg"
              size="large"
              allowClear
            />
          </Tooltip>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Data Inicial
          </label>
          <DatePicker
            value={filters.startDate ? dayjs(filters.startDate) : null}
            onChange={handleStartDateChange}
            format="DD/MM/YYYY"
            placeholder="Data inicial"
            className="!w-full rounded-lg"
            size="large"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Data Final
          </label>
          <DatePicker
            value={filters.endDate ? dayjs(filters.endDate) : null}
            onChange={handleEndDateChange}
            format="DD/MM/YYYY"
            placeholder="Data final"
            className="!w-full rounded-lg"
            size="large"
          />
        </div>

        <div className="flex items-center gap-2">
          <Tooltip title="Limpar filtros">
            <Button
              icon={<ClearOutlined />}
              onClick={onClear}
              size="large"
              className="rounded-lg"
              style={{ borderColor: '#101010', color: '#101010', backgroundColor: 'transparent', boxShadow: 'none' }}
            >
              Limpar
            </Button>
          </Tooltip>

          <Button
            icon={<FilterOutlined />}
            onClick={onApply}
            loading={isLoading}
            size="large"
            className="rounded-lg !border-0"
            style={{ backgroundColor: '#101010', color: '#fff', boxShadow: 'none' }}
          >
            Filtrar
          </Button>
        </div>
      </div>
    </div>
  );
}
